// src/DevDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Copy } from "lucide-react";
import UploadBuild from "./UploadBuild";

const EXPIRED_BUILDS_PAGE_SIZE = 3;

// Snippets devs can copy for ready-signal
const PLAYSET_READY_SNIPPETS = {
  godot: `# Godot 4.x (Web export)
func _ready() -> void:
    if OS.get_name() == "Web" and Engine.has_singleton("JavaScriptBridge"):
        JavaScriptBridge.eval(
            "if (window.playsetGameReady) window.playsetGameReady();",
            false
        )
`,
  unity: `// Unity (WebGL)
//
// 1. Put this in a C# script on a GameObject that exists when your game is ready.
// 2. Make sure the script is included in the WebGL build.

using System.Runtime.InteropServices;
using UnityEngine;

public class PlaysetReady : MonoBehaviour
{
    #if UNITY_WEBGL && !UNITY_EDITOR
    [DllImport("__Internal")]
    private static extern void playsetGameReady();
    #endif

    void Start()
    {
        #if UNITY_WEBGL && !UNITY_EDITOR
        try { playsetGameReady(); } catch {}
        #endif
    }
}
`,
  js: `// Plain JavaScript / HTML5
// Call this once your first playable scene is ready.

if (window.playsetGameReady) {
  window.playsetGameReady();
}

// Example: after your assets are loaded
// loader.onComplete = () => {
//   startGame();
//   if (window.playsetGameReady) window.playsetGameReady();
// };
`,
};

function sessionSeconds(s) {
  // Prefer explicit recording duration if you store it
  if (
    typeof s.recording_duration_seconds === "number" &&
    s.recording_duration_seconds > 0
  ) {
    return s.recording_duration_seconds;
  }

  const startIso = s.actual_started_at || s.started_at;
  const endIso = s.ended_at;

  if (!startIso || !endIso) return null;

  const start = new Date(startIso).getTime();
  const end = new Date(endIso).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;

  return Math.round((end - start) / 1000);
}

function fmtDuration(seconds) {
  if (seconds == null) return "—";
  const s = Math.max(0, seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;

  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, "0")}s`;
  return `${sec}s`;
}

function truncId(v, n = 8) {
  if (!v) return "—";
  if (v.length <= n) return v;
  return `${v.slice(0, n)}…`;
}

function HoverTip({ text, children }) {
  if (!text) return children;
  return (
    <span className="relative inline-flex items-center group">
      {children}
      <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 rounded-md bg-[#fefefe] text-[#1f1f1f] text-xs font-nunito whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
        {text}
      </span>
    </span>
  );
}



export default function DevDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();

  // gameId from ?game= query param
  const search = new URLSearchParams(location.search);
  const selectedGameId = search.get("game") || null;

  const [game, setGame] = useState(null);
  const [builds, setBuilds] = useState([]);
  const [testers, setTesters] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [sessionsOffset, setSessionsOffset] = useState(0);
  const [sessionsPageSize] = useState(5);
  const [hasMoreSessions, setHasMoreSessions] = useState(true);
  const [copied, setCopied] = useState(false);
  const [invites, setInvites] = useState([]);
  const [highlightBuildId, setHighlightBuildId] = useState(null);
  const [expiredShowCount, setExpiredShowCount] = useState(0);

  const [games, setGames] = useState([]);
  const [loadingGames, setLoadingGames] = useState(false);
  const [loadingGame, setLoadingGame] = useState(false);

  // plan & per-tester allocation state
  const [planUsage, setPlanUsage] = useState(null);
  const [testerAllocations, setTesterAllocations] = useState({});
  const [allocError, setAllocError] = useState("");
  const [savingAlloc, setSavingAlloc] = useState(false);

  // edit game modal state
  const [showEditGame, setShowEditGame] = useState(false);
  const [editGameName, setEditGameName] = useState("");
  const [editDeveloperName, setEditDeveloperName] = useState("");
  const [editRequiresReadySignal, setEditRequiresReadySignal] = useState(false);
  const [savingGame, setSavingGame] = useState(false);
  const [editGameError, setEditGameError] = useState("");
  const [snippetCopied, setSnippetCopied] = useState(null);
  
  // delete game modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingGame, setDeletingGame] = useState(false);
  const [deleteGameError, setDeleteGameError] = useState("");

  // subscription access state
  const [subscriptionAccess, setSubscriptionAccess] = useState(null);
  const [subscriptionAccessLoading, setSubscriptionAccessLoading] = useState(false);

  const [copiedKey, setCopiedKey] = useState(null);

  // Check subscription access status
  useEffect(() => {
    if (!currentUser) {
      setSubscriptionAccess(null);
      return;
    }

    let cancelled = false;

    (async () => {
      setSubscriptionAccessLoading(true);
      try {
        const { data, error } = await supabase.rpc(
          "check_subscription_access",
          { p_user_id: currentUser.id }
        );

        if (error) {
          console.error("Error checking subscription access:", error);
          if (!cancelled) setSubscriptionAccess({ can_access: true }); // Default to allow
        } else if (!cancelled) {
          setSubscriptionAccess(data);
        }
      } catch (e) {
        console.error("Unexpected error checking subscription access:", e);
        if (!cancelled) setSubscriptionAccess({ can_access: true }); // Default to allow
      } finally {
        if (!cancelled) setSubscriptionAccessLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  // Load all games for this owner (for the /dashboard list view)
  useEffect(() => {
    if (!currentUser) {
      setGames([]);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoadingGames(true);
      try {
        const { data, error } = await supabase
          .from("games")
          .select("*")
          .eq("owner_id", currentUser.id)
          .order("created_at", { ascending: true });

        if (error) {
          console.error("Error loading games for dashboard:", error);
          if (!cancelled) setGames([]);
        } else if (!cancelled) {
          setGames(data || []);
        }
      } catch (e) {
        if (!cancelled) {
          console.error("Unexpected error loading games for dashboard:", e);
          setGames([]);
        }
      } finally {
        if (!cancelled) setLoadingGames(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  // Reset expired list pagination when game changes
  useEffect(() => {
    setExpiredShowCount(0);
  }, [game?.id]);

  // Load the selected game when ?game=<id> is present
  useEffect(() => {
    if (!currentUser) {
      setGame(null);
      setLoadingGame(false);
      return;
    }

    if (!selectedGameId) {
      setGame(null);
      setLoadingGame(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoadingGame(true);
      try {
        const { data, error } = await supabase
          .from("games")
          .select("*")
          .eq("id", selectedGameId)
          .eq("owner_id", currentUser.id)
          .single();

        if (cancelled) return;

        if (error) {
          console.error("Error loading selected game:", error);
          setGame(null);
        } else {
          setGame(data);
        }

        try {
          const sessionInfo = await supabase.auth.getSession();
          const userInfo = await supabase.auth.getUser();
          console.debug(
            "DevDashboard auth debug: currentUser, sessionInfo, userInfo",
            currentUser,
            sessionInfo,
            userInfo
          );
        } catch (e) {
          console.warn("DevDashboard: failed to read supabase auth state", e);
        }
      } catch (e) {
        if (!cancelled) {
          console.error("Unexpected error choosing game for dashboard:", e);
          setGame(null);
        }
      } finally {
        if (!cancelled) setLoadingGame(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUser, selectedGameId]);

  // Load initial builds / testers / sessions / invites / plan usage
  useEffect(() => {
    if (!game) return;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("builds")
          .select("*")
          .eq("game_id", game.id)
          .order("uploaded_at", { ascending: false });
        if (error) {
          console.error("Error loading builds:", error);
        } else {
          console.debug("Loaded builds:", data);
          setBuilds(data || []);
        }

        const { data: tdata, error: terr } = await supabase
          .from("testers")
          .select("*")
          .eq("game_id", game.id);
        if (terr) {
          console.error("Error loading testers:", terr);
          setTesters([]);
          setTesterAllocations({});
        } else {
          setTesters(tdata || []);

          let usageByEmail = {};
          try {
            const { data: usageRows, error: usageErr } = await supabase
              .from("v_usage")
              .select("email, seconds_limit, seconds_used")
              .eq("game_id", game.id);

            if (usageErr) {
              console.warn("Error loading v_usage:", usageErr);
            } else if (usageRows) {
              usageByEmail = Object.fromEntries(
                usageRows.map((u) => [u.email, u])
              );
            }
          } catch (e) {
            console.warn("Unexpected error loading v_usage:", e);
          }

          setTesterAllocations(
            Object.fromEntries(
              (tdata || []).map((t) => {
                const usage = usageByEmail[t.email] || {};
                const secondsLimit =
                  typeof usage.seconds_limit === "number"
                    ? usage.seconds_limit
                    : t.seconds_limit || null;
                const minutes =
                  secondsLimit != null ? Math.round(secondsLimit / 60) : 0;
                const usedSeconds = usage.seconds_used || 0;

                return [
                  t.id,
                  {
                    minutes,
                    usedSeconds,
                  },
                ];
              })
            )
          );
        }

        const start = 0;
        const end = sessionsPageSize - 1;
        const { data: sdata, error: serr } = await supabase
          .from("sessions")
          .select("*")
          .eq("game_id", game.id)
          .order("started_at", { ascending: false })
          .range(start, end);
        if (serr) console.error("Error loading sessions:", serr);
        else {
          setSessions(sdata || []);
          setSessionsOffset((sdata || []).length);
          setHasMoreSessions((sdata || []).length === sessionsPageSize);
        }

        const { data: invdata, error: inverr } = await supabase
          .from("invites")
          .select("*")
          .eq("game_id", game.id)
          .order("created_at", { ascending: false });

        if (inverr) {
          console.error("Error loading invites:", inverr);
        } else {
          setInvites(invdata || []);
        }

        try {
          const { data: usage, error: usageErr } = await supabase.rpc(
            "get_plan_usage_for_user",
            { p_owner_id: game.owner_id }
          );
          if (usageErr) {
            console.error("Error loading plan usage:", usageErr);
          } else {
            const value = Array.isArray(usage) ? usage[0] : usage;
            setPlanUsage(value || null);
          }
        } catch (e) {
          console.error("Unexpected error loading plan usage:", e);
        }
      } catch (e) {
        console.error("Unexpected error loading dashboard data:", e);
      }
    })();
  }, [game, sessionsPageSize]);

  // Realtime updates for builds
  useEffect(() => {
    if (!game?.id) return;

    const channel = supabase
      .channel(`builds-game-${game.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "builds",
          filter: `game_id=eq.${game.id}`,
        },
        (payload) => {
          const { eventType, new: newRow, old } = payload;
          setBuilds((prev) => {
            if (eventType === "INSERT") {
              const exists = prev.some((b) => b.id === newRow.id);
              if (exists) {
                return prev.map((b) => (b.id === newRow.id ? newRow : b));
              }
              const updated = [newRow, ...prev];
              updated.sort(
                (a, b) =>
                  new Date(b.uploaded_at).getTime() -
                  new Date(a.uploaded_at).getTime()
              );
              return updated;
            }
            if (eventType === "UPDATE") {
              return prev.map((b) => (b.id === newRow.id ? newRow : b));
            }
            if (eventType === "DELETE") {
              return prev.filter((b) => b.id !== old.id);
            }
            return prev;
          });

          if (eventType === "INSERT" && newRow.status === "Live") {
            setGame((g) =>
              g ? { ...g, current_build_id: newRow.id } : g
            );
            setHighlightBuildId(newRow.id);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [game?.id]);

  // Realtime updates for sessions list
  useEffect(() => {
    if (!game?.id) return;

    const channel = supabase
      .channel(`sessions-game-${game.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "sessions",
          filter: `game_id=eq.${game.id}`,
        },
        (payload) => {
          const { eventType, new: newRow, old } = payload;
          setSessions((prev) => {
            if (eventType === "INSERT") {
              const exists = prev.some((s) => s.id === newRow.id);
              if (exists) {
                return prev.map((s) => (s.id === newRow.id ? newRow : s));
              }
              return [newRow, ...prev].slice(
                0,
                Math.max(prev.length, sessionsPageSize)
              );
            }
            if (eventType === "UPDATE") {
              return prev.map((s) => (s.id === newRow.id ? newRow : s));
            }
            if (eventType === "DELETE") {
              return prev.filter((s) => s.id !== old.id);
            }
            return prev;
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [game?.id, sessionsPageSize]);

  const handleCopySnippet = async (key) => {
    const text = PLAYSET_READY_SNIPPETS[key];
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setSnippetCopied(key);
      setTimeout(() => setSnippetCopied(null), 1500);
    } catch (e) {
      console.error("Failed to copy snippet:", e);
    }
  };

  const copyToClipboard = async (text, key) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1200);
    } catch {
      window.prompt("Copy this value:", text);
    }
  };


  // Local handler when new build uploaded (for instant UI update)
  const handleBuildUploaded = (build) => {
    if (!build) return;
    setBuilds((prev) => {
      const others = prev
        .filter((b) => b.id !== build.id)
        .map((b) =>
          b.status === "Live" ? { ...b, status: "Expired" } : b
        );
      const next = [build, ...others];
      next.sort(
        (a, b) =>
          new Date(b.uploaded_at).getTime() -
          new Date(a.uploaded_at).getTime()
      );
      return next;
    });
    setGame((g) => (g ? { ...g, current_build_id: build.id } : g));
    setHighlightBuildId(build.id);
  };

  useEffect(() => {
    if (!highlightBuildId) return;
    const t = setTimeout(() => setHighlightBuildId(null), 2000);
    return () => clearTimeout(t);
  }, [highlightBuildId]);

  const latestLiveBuild = useMemo(
    () => builds.find((b) => b.status === "Live"),
    [builds]
  );

  const currentBuild = useMemo(() => {
    if (!builds || builds.length === 0) return null;
    if (game?.current_build_id) {
      return (
        builds.find((b) => b.id === game.current_build_id) ||
        builds.find((b) => b.status === "Live") ||
        builds[0]
      );
    }
    return builds.find((b) => b.status === "Live") || builds[0];
  }, [builds, game]);

  const expiredBuilds = useMemo(() => {
    if (!currentBuild) {
      return builds.filter((b) => b.status === "Expired");
    }
    return builds.filter(
      (b) => b.id !== currentBuild.id && b.status === "Expired"
    );
  }, [builds, currentBuild]);

  const visibleExpiredBuilds = useMemo(() => {
    if (expiredShowCount <= 0) return [];
    return expiredBuilds.slice(0, expiredShowCount);
  }, [expiredBuilds, expiredShowCount]);

  const testerRows = useMemo(() => {
    const map = new Map();

    invites.forEach((inv) => {
      const existing = map.get(inv.email) || {};
      map.set(inv.email, { ...existing, invite: inv });
    });

    testers.forEach((t) => {
      const existing = map.get(t.email) || {};
      map.set(t.email, { ...existing, tester: t });
    });

    return Array.from(map.values());
  }, [invites, testers]);

  const totalAllocatedMinutes = useMemo(() => {
    return Object.values(testerAllocations).reduce(
      (sum, a) => sum + (a.minutes || 0),
      0
    );
  }, [testerAllocations]);

  const planLimitSeconds = useMemo(() => {
    if (!planUsage || typeof planUsage.seconds_limit !== "number") return null;
    return planUsage.seconds_limit;
  }, [planUsage]);

  const planLimitMinutes = useMemo(() => {
    return planLimitSeconds == null ? null : Math.floor(planLimitSeconds / 60);
  }, [planLimitSeconds]);

  // Sum tester-used seconds from what you already load into testerAllocations
  const totalUsedSecondsFromTesters = useMemo(() => {
    return Object.values(testerAllocations).reduce(
      (sum, a) => sum + (a.usedSeconds || 0),
      0
    );
  }, [testerAllocations]);

  // If your RPC already returns seconds_used, prefer it. Otherwise fall back to sum.
  const planUsedSeconds = useMemo(() => {
    if (planUsage && typeof planUsage.seconds_used === "number") {
      return planUsage.seconds_used;
    }
    return totalUsedSecondsFromTesters;
  }, [planUsage, totalUsedSecondsFromTesters]);

  const remainingToAllocateMinutes = useMemo(() => {
    if (planLimitMinutes == null) return null;
    return planLimitMinutes - totalAllocatedMinutes; // can be negative if over-allocated
  }, [planLimitMinutes, totalAllocatedMinutes]);

  const remainingOnPlanSeconds = useMemo(() => {
    if (planLimitSeconds == null) return null;
    return Math.max(0, planLimitSeconds - (planUsedSeconds || 0));
  }, [planLimitSeconds, planUsedSeconds]);

  const saveTesterAllocations = async () => {
    if (!testers.length || !game) return;
    setAllocError("");

    const payload = testers.map((t) => {
      const alloc = testerAllocations[t.id] || { minutes: 0, usedSeconds: 0 };
      return {
        email: t.email,
        minutes: alloc.minutes || 0,
      };
    });

    setSavingAlloc(true);
    try {
      const { error } = await supabase.rpc("save_tester_allocations", {
        p_game_id: game.id,
        p_allocations: payload,
      });

      if (error) {
        console.error("save_tester_allocations error:", error);
        setAllocError(error.message || "Failed to save allocations.");
        return;
      }

      // Refresh like you already do:
      const { data: refreshed, error: terr } = await supabase
        .from("testers")
        .select("*")
        .eq("game_id", game.id);

      if (!terr && refreshed) {
        setTesters(refreshed);

        const { data: usageRows, error: usageErr } = await supabase
          .from("v_usage")
          .select("email, seconds_limit, seconds_used")
          .eq("game_id", game.id);

        const usageByEmail =
          usageErr || !usageRows
            ? {}
            : Object.fromEntries(usageRows.map((u) => [u.email, u]));

        setTesterAllocations(
          Object.fromEntries(
            refreshed.map((t) => {
              const usage = usageByEmail[t.email] || {};
              const secondsLimit =
                typeof usage.seconds_limit === "number"
                  ? usage.seconds_limit
                  : t.seconds_limit || null;

              const minutes = secondsLimit != null ? Math.round(secondsLimit / 60) : 0;
              const usedSeconds = usage.seconds_used || 0;

              return [t.id, { minutes, usedSeconds }];
            })
          )
        );
      }
    } catch (e) {
      console.error("Failed to save allocations:", e);
      setAllocError("Failed to save allocations. Please try again.");
    } finally {
      setSavingAlloc(false);
    }
  };


  const expireBuild = async (buildId) => {
    try {
      const { error } = await supabase
        .from("builds")
        .update({ status: "Expired" })
        .eq("id", buildId);
      if (error) throw error;
      if (game?.current_build_id === buildId) {
        await supabase
          .from("games")
          .update({ current_build_id: null })
          .eq("id", game.id);
        setGame((g) => ({ ...g, current_build_id: null }));
      }
      setBuilds((prev) =>
        prev.map((b) =>
          b.id === buildId ? { ...b, status: "Expired" } : b
        )
      );
    } catch (e) {
      console.error("Failed to expire build:", e);
      alert("Failed to expire build");
    }
  };

  const loadMoreSessions = async () => {
    if (!game) return;
    try {
      const start = sessionsOffset;
      const end = start + sessionsPageSize - 1;
      const { data: more, error } = await supabase
        .from("sessions")
        .select("*")
        .eq("game_id", game.id)
        .order("started_at", { ascending: false })
        .range(start, end);
      if (error) {
        console.error("Error loading more sessions:", error);
        return;
      }

      if (!more || more.length === 0) {
        setHasMoreSessions(false);
        return;
      }

      setSessions((prev) => {
        const ids = new Set(prev.map((p) => p.id));
        const toAdd = more.filter((m) => !ids.has(m.id));
        return [...prev, ...toAdd];
      });

      setSessionsOffset((o) => o + (more ? more.length : 0));
      setHasMoreSessions((more ? more.length : 0) === sessionsPageSize);
    } catch (e) {
      console.error("Unexpected error loading more sessions:", e);
    }
  };

  const shareUrl = game
    ? `${window.location.origin}/play?game=${game.id}`
    : "";

  const handleCopySharableLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      window.prompt("Copy this link:", shareUrl);
    }
  };

  const openEditGame = () => {
    if (!game) return;
    setEditGameName(game.name || "");
    setEditDeveloperName(game.developer_name || "");
    setEditRequiresReadySignal(!!game.require_ready_signal);
    setEditGameError("");
    setShowEditGame(true);
  };

  const closeEditGame = () => {
    if (savingGame) return;
    setShowEditGame(false);
  };

  const saveGameDetails = async (e) => {
    e.preventDefault();
    if (!game) return;
    setSavingGame(true);
    setEditGameError("");
    try {
      const updates = {
        name: (editGameName || "").trim() || "Untitled game",
        developer_name: (editDeveloperName || "").trim() || null,
        require_ready_signal: editRequiresReadySignal,
      };

      const { data, error } = await supabase
        .from("games")
        .update(updates)
        .eq("id", game.id)
        .select()
        .single();

      if (error) throw error;

      setGame((g) => (g ? { ...g, ...data } : g));
      setShowEditGame(false);
    } catch (err) {
      console.error("Failed to save game details:", err);
      setEditGameError("Failed to save. Please try again.");
    } finally {
      setSavingGame(false);
    }
  };

  const deleteGame = async () => {
    if (!game || !currentUser) return;
    setDeletingGame(true);
    setDeleteGameError("");
    try {
      const { data, error } = await supabase.rpc("delete_game_cascade", {
        p_game_id: game.id,
        p_user_id: currentUser.id,
      });

      if (error) {
        console.error("Failed to delete game via RPC:", error);
        setDeleteGameError("Failed to delete game. Please try again.");
        return;
      }

      if (!data || !data[0]?.success) {
        setDeleteGameError(data?.[0]?.message || "Failed to delete game");
        return;
      }

      // Close modals
      setShowDeleteConfirm(false);
      setShowEditGame(false);

      // Remove from games list
      setGames((prev) => prev.filter((g) => g.id !== game.id));

      // Reset to main dashboard
      setGame(null);
      navigate("/dashboard");
    } catch (err) {
      console.error("Failed to delete game (unexpected):", err);
      setDeleteGameError("Unexpected error deleting game. Please try again.");
    } finally {
      setDeletingGame(false);
    }
  };

  return (
    <div className="ml-0 md:ml-0 flex-1 flex flex-col justify-start bg-[#111] text-[#eee] font-nunito">
      <style>{`
      @keyframes gold-shimmer {
        0%   { box-shadow: 0 0 0 rgba(250,204,21,0); border-color: #fbbf24; }
        50%  { box-shadow: 0 0 18px rgba(250,204,21,0.9); border-color: #facc15; }
        100% { box-shadow: 0 0 0 rgba(250,204,21,0); border-color: #fbbf24; }
      }
      .gold-shimmer {
        animation: gold-shimmer 1.2s ease-out 0s 2;
      }
    `}</style>

      {!selectedGameId ? (
        <div className="w-full mx-auto p-0 space-y-4">
          <div className="flex items-start justify-between gap-6">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">
                Developer Dashboard
              </h1>

              <h2 className="text-lg font-semibold mt-6">My Games</h2>
              <p className="text-sm text-[#aaa] mt-2">
                Choose a game to manage builds, testers, and playtest sessions.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Link
                to="/dashboard/games/new"
                className="text-base px-3 py-1.5 rounded-lg border border-[#8338ec] bg-[#8338ec]/20 text-[#e0f2fe] hover:bg-[#8338ec] transition"
              >
                Add new game
              </Link>
            </div>
          </div>

          {loadingGames ? (
            <div className="text-sm text-[#777]">Loading your games…</div>
          ) : games.length === 0 ? (
            <div className="flex flex-col items-start gap-3">
              <p className="text-sm text-[#777]">
                You don&apos;t have any games yet.
              </p>
              <Link
                to="/dashboard/games/new"
                className="inline-flex items-center text-xs px-3 py-1.5 rounded-lg border border-[#8338ec] bg-[#8338ec]/20 text-[#e0f2fe] hover:bg-[#38bdf8] hover:text-black transition"
              >
                Add your first game
              </Link>
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              {games.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => navigate(`/dashboard?game=${g.id}`)}
                  className="w-full text-left rounded-xl border border-[#252525] bg-[#111] px-4 py-3 hover:border-[#8338ec] hover:bg-[#181818] transition flex flex-col gap-1"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-base font-semibold truncate">
                      {g.name || "Untitled game"}
                    </span>
                  </div>
                  {g.developer_name && (
                    <span className="text-sm text-[#fff]/60 truncate">
                      by {g.developer_name}
                    </span>
                  )}
                  <span className="text-xs text-[#fff]/60">
                    Added{" "}
                    {g.created_at
                      ? new Date(g.created_at).toLocaleDateString()
                      : "—"}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          {loadingGame && !game && (
            <div className="w-full mx-auto p-6 text-sm text-[#777]">
              Loading game…
            </div>
          )}

          {!loadingGame && !game && (
            <div className="w-full mx-auto p-6 text-sm text-[#fca5a5]">
              Game not found or you don&apos;t have access to it.
              <button
                type="button"
                onClick={() => navigate("/dashboard")}
                className="mt-3 inline-flex items-center text-xs px-3 py-1.5 rounded-lg border border-[#444] text-[#ddd] hover:border-[#38bdf8] hover:text-[#38bdf8]"
              >
                Back to games list
              </button>
            </div>
          )}

          {game && (
            <div className="w-full mx-auto p-0 space-y-8">
              <div className="flex items-start justify-between gap-6">
                <div>
                  <div className="flex flex-row items-center gap-x-4">
                    <h1 className="text-2xl font-semibold tracking-tight">
                      {game.name}
                    </h1>
                  </div>
                  <p className="text-sm text-[#aaa] mt-2">
                    {game.developer_name && (
                      <span className="block text-xs text-[#888] mb-1">
                        by {game.developer_name}
                      </span>
                    )}
                    Manage your builds, testers, and playtest sessions.
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={openEditGame}
                    className="text-xs px-3 py-1.5 rounded-lg border border-[#444] bg-[#444]/20 text-[#ddd] hover:border-[#444] hover:bg-[#444]"
                  >
                    Edit game
                  </button>
                </div>
              </div>

              {/* Upload / Builds */}
              <section className="bg-[#151515] border border-[#252525] rounded-xl p-4 md:p-5 space-y-4">
                <div className="flex items-start justify-between">
                  <h2 className="text-lg font-semibold">Builds</h2>
                  <UploadBuild gameId={game.id} onUploaded={handleBuildUploaded} />
                </div>
                <div className="mt-4">
                  {currentBuild ? (
                    <div
                      className={
                        "p-4 bg-[#fff]/5 rounded-lg border " +
                        (highlightBuildId === currentBuild.id
                          ? "border-yellow-400 gold-shimmer"
                          : "border-[#222]")
                      }
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex flex-col">
                            <div className="flex flex-row items-center gap-2">
                              <h3 className="text-base font-semibold">
                                Current Build
                              </h3>
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${currentBuild.status === "Live"
                                  ? "bg-green-900/40 text-green-300"
                                  : "bg-red-900/40 text-red-300"
                                  }`}
                              >
                                {currentBuild.status}
                              </span>
                            </div>

                            <div className="flex flex-row gap-x-4 mt-2">
                              <div className="text-xs text-[#fff]/80">
                                {currentBuild.id}
                              </div>
                              <div className="text-xs text-[#fff]/80">
                                &bull;
                              </div>
                              <div className="text-xs text-[#fff]/80">
                                Uploaded{" "}
                                {new Date(
                                  currentBuild.uploaded_at
                                ).toLocaleString()}
                              </div>
                            </div>
                          </div>

                          {latestLiveBuild && (
                            <div className="flex flex-col gap-1 text-left mt-2">
                              <div className="mt-1 flex items-center gap-2 w-full">
                                <button
                                  type="button"
                                  onClick={() =>
                                    navigate("/play?game=" + game.id)
                                  }
                                  className="h-8 flex-1 flex items-center bg-[#fff]/10 border border-[#333] rounded-lg px-3 py-1.5 text-xs text-[#ddd] overflow-hidden whitespace-nowrap text-ellipsis hover:bg-[#262626] hover:border-[#555] transition cursor-pointer text-left"
                                >
                                  {shareUrl || "No link available"}
                                </button>
                                <button
                                  onClick={handleCopySharableLink}
                                  className="h-8 w-8 shrink-0 inline-flex items-center justify-center p-2 rounded-lg border border-[#8338ec] bg-[#8338ec]/15 hover:bg-[#8338ec] hover:text-white transition"
                                  aria-label="Copy sharable link"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                                <span className="text-[10px] h-3 text-right">
                                  {copied && (
                                    <span className="text-green-300">
                                      Link copied!
                                    </span>
                                  )}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-center gap-2 self-start">
                          {currentBuild.status !== "Expired" && (
                            <button
                              onClick={() => expireBuild(currentBuild.id)}
                              className="text-sm px-3 py-1.5 rounded-lg border border-[#f87171] bg-[#f87171]/10 text-[#f87171] hover:bg-[#2b0a0a]"
                            >
                              Expire
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="px-4 py-6 text-center text-[#777] text-sm">
                      No builds yet.
                    </div>
                  )}

                  {builds.length > 1 && expiredBuilds.length > 0 && (
                    <>
                      {expiredShowCount === 0 ? (
                        <div className="mt-4 flex items-center justify-center">
                          <button
                            onClick={() =>
                              setExpiredShowCount(EXPIRED_BUILDS_PAGE_SIZE)
                            }
                            className="text-sm px-3 py-1.5 rounded-lg border border-[#444] text-[#ddd] hover:border-[#38bdf8] hover:text-[#38bdf8]"
                          >
                            See expired builds
                          </button>
                        </div>
                      ) : (
                        <>
                          <h3 className="text-base font-semibold mt-6">
                            Expired Builds
                          </h3>

                          <p className="text-xs text-[#fff]/80 mt-2">
                            Expired builds are deleted from storage. Please keep
                            your own backups or version control — Playset
                            isn&apos;t a replacement for them.
                          </p>

                          <div className="mt-4 overflow-x-auto">
                            <table className="min-w-full text-sm border-collapse">
                              <thead>
                                <tr className="bg-[#1b1b1b] text-xs uppercase tracking-wide text-[#aaa]">
                                  <th className="px-3 py-2 text-left">Build ID</th>
                                  <th className="px-3 py-2 text-left">
                                    Uploaded
                                  </th>
                                  <th className="px-3 py-2 text-left">
                                    Status
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {visibleExpiredBuilds.map((b, idx) => (
                                  <tr
                                    key={b.id}
                                    className={
                                      idx % 2 === 0
                                        ? "bg-[#111]"
                                        : "bg-[#151515]"
                                    }
                                  >
                                    <td className="px-3 py-2 whitespace-nowrap">
                                      <span className="font-medium">
                                        {b.id}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2">
                                      {new Date(
                                        b.uploaded_at
                                      ).toLocaleString()}
                                    </td>
                                    <td className="px-3 py-2">
                                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-red-900/40 text-red-300">
                                        {b.status}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {expiredBuilds.length > EXPIRED_BUILDS_PAGE_SIZE && (
                            <div className="mt-3 flex items-center justify-center">
                              {expiredBuilds.length > expiredShowCount ? (
                                <button
                                  onClick={() =>
                                    setExpiredShowCount(
                                      (c) => c + EXPIRED_BUILDS_PAGE_SIZE
                                    )
                                  }
                                  className="text-sm px-3 py-1.5 rounded-lg border border-[#444] text-[#ddd] hover:border-[#38bdf8] hover:text-[#38bdf8]"
                                >
                                  See more expired builds
                                </button>
                              ) : (
                                <div className="text-xs text-[#777]">
                                  No more expired builds
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </section>

              {/* Testers */}
              <section className="bg-[#151515] border border-[#252525] rounded-xl p-4 md:p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <h2 className="text-lg font-semibold">Testers</h2>
                    <p className="text-sm text-[#fff]/80">
                      See invite status and allocate time per tester.
                    </p>
                  </div>

                  <Link
                    to="/dashboard/testers/invite"
                    state={{ gameId: game.id }}
                    className="text-sm px-3 py-1.5 rounded-lg border border-[#8338ec] bg-[#8338ec]/20 hover:bg-[#8338ec] text-white transition"
                  >
                    Invite tester
                  </Link>
                </div>

                <div className="mt-4">
                  {allocError && (
                    <div className="mb-3 text-xs text-red-400 bg-red-900/30 border border-red-700 rounded px-3 py-2">
                      {allocError}
                    </div>
                  )}

                  {testerRows.length === 0 ? (
                    <div className="text-xs text-[#777]">
                      No testers or invites yet. Invite someone above to get
                      started.
                    </div>
                  ) : (
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-[#1b1b1b] text-xs uppercase tracking-wide text-[#aaa]">
                          <th className="px-3 py-2 text-left">Tester</th>
                          <th className="px-3 py-2 text-left">Status</th>
                          <th className="px-3 py-2 text-left">
                            Allocated (min)
                          </th>
                          <th className="px-3 py-2 text-left">Used</th>
                        </tr>
                      </thead>
                      <tbody>
                        {testerRows.map((row, idx) => {
                          const { tester, invite } = row;
                          const email = tester?.email || invite?.email;
                          const invitedAt = invite?.created_at
                            ? new Date(invite.created_at).toLocaleDateString()
                            : null;
                          const acceptedAt = invite?.accepted_at
                            ? new Date(invite.accepted_at).toLocaleString()
                            : null;
                          const isAccepted = !!(invite?.accepted_at || tester);

                          const alloc = tester
                            ? testerAllocations[tester.id] || {
                              minutes: 0,
                              usedSeconds: 0,
                            }
                            : { minutes: 0, usedSeconds: 0 };

                          return (
                            <tr
                              key={email + (tester?.id || "")}
                              className="border-b border-[#1b1b1b]"
                              className={
                                idx % 2 === 0 ? "bg-[#111]" : "bg-[#151515]"
                              }
                            >
                              <td className="px-3 py-2">
                                <div className="text-sm text-[#eee]">
                                  {email}
                                </div>
                                {invitedAt && (
                                  <div className="text[11px] text-[#777]">
                                    Invited {invitedAt}
                                  </div>
                                )}
                                {acceptedAt && (
                                  <div className="text-[11px] text-[#777]">
                                    Accepted {acceptedAt}
                                  </div>
                                )}
                              </td>

                              <td className="px-3 py-2">
                                <span
                                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${isAccepted
                                    ? "bg-green-900/40 text-green-300"
                                    : "bg-yellow-900/40 text-yellow-300"
                                    }`}
                                >
                                  {isAccepted ? "Accepted" : "Pending"}
                                </span>
                              </td>

                              <td className="px-3 py-2">
                                <input
                                  type="number"
                                  min={Math.ceil((alloc.usedSeconds || 0) / 60)}
                                  value={alloc.minutes}
                                  disabled={!tester}
                                  onChange={(e) => {
                                    if (!tester) return;
                                    const usedMin = Math.ceil((alloc.usedSeconds || 0) / 60);
                                    const minutes = Math.max(
                                      usedMin,
                                      parseInt(e.target.value || "0", 10)
                                    );
                                    setTesterAllocations((prev) => ({
                                      ...prev,
                                      [tester.id]: {
                                        ...prev[tester.id],
                                        minutes,
                                        usedSeconds: alloc.usedSeconds,
                                      },
                                    }));
                                  }}
                                  className="w-20 rounded bg-[#222] border border-[#333] px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#38bdf8] disabled:opacity-50 disabled:cursor-not-allowed"
                                />
                              </td>

                              <td className="px-3 py-2" title={`${alloc.usedSeconds || 0}s`}>
                                {alloc.usedSeconds ? fmtDuration(alloc.usedSeconds) : "0s"}
                              </td>

                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}

                  {testers.length > 0 && (
                    <div className="mt-3 flex items-center justify-between">
                      {planLimitMinutes != null && (
                        <div className="text-sm text-[#aaa]">
                          Allocated{" "}
                          <span className="text-[#fff] font-semibold">{totalAllocatedMinutes}</span>
                          {" / "}
                          {planLimitMinutes} min plan

                          {/* Option 1: Remaining to allocate */}
                          {remainingToAllocateMinutes != null && (
                            <>
                              {" • "}
                              {remainingToAllocateMinutes >= 0 ? (
                                <>
                                  <span className="text-[#fff] font-semibold">
                                    {remainingToAllocateMinutes}
                                  </span>{" "}
                                  min left to allocate
                                </>
                              ) : (
                                <>
                                  <span className="text-red-300 font-semibold">
                                    {Math.abs(remainingToAllocateMinutes)}
                                  </span>{" "}
                                  min over-allocated
                                </>
                              )}
                            </>
                          )}

                          {/* Option 2: Used by testers */}
                          {" • "}
                          <span className="text-[#fff] font-semibold">
                            {fmtDuration(planUsedSeconds || 0)}
                          </span>{" "}
                          used

                          {/* Optional: Remaining on plan */}
                          {remainingOnPlanSeconds != null && (
                            <>
                              {" • "}
                              <span className="text-[#fff] font-semibold">
                                {fmtDuration(remainingOnPlanSeconds)}
                              </span>{" "}
                              remaining
                            </>
                          )}
                        </div>
                      )}


                      <button
                        type="button"
                        onClick={saveTesterAllocations}
                        disabled={savingAlloc}
                        className="text-sm px-3 py-1.5 rounded-lg border border-[#444] text-[#ddd] hover:border-[#38bdf8] hover:text-[#38bdf8] disabled:opacity-60"
                      >
                        {savingAlloc ? "Saving…" : "Save allocations"}
                      </button>
                    </div>
                  )}
                </div>
              </section>

              {/* Sessions */}
              <section className="bg-[#151515] border border-[#252525] rounded-xl p-4 md:p-5 space-y-4 mb-10">
                <div className="space-y-2">
                  <h2 className="text-lg font-semibold">Playtest sessions</h2>

                  <p className="text-sm text-[#fff]/80">
                    Status is best-effort: sessions are marked as ended when the
                    tester closes the play page or you end them manually.
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-[#1b1b1b] text-xs uppercase tracking-wide text-[#aaa]">
                        <th className="px-3 py-2 text-left">Session ID</th>
                        <th className="px-3 py-2 text-left">Build ID</th>
                        <th className="px-3 py-2 text-left">Tester</th>
                        <th className="px-3 py-2 text-left">Started</th>
                        <th className="px-3 py-2 text-left">Duration</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessions.map((s, idx) => {
                        const isActive = !s.ended_at;
                        const secs = sessionSeconds(s);
                        const durationLabel = fmtDuration(secs);

                        return (
                          <tr
                            key={s.id}
                            className={
                              idx % 2 === 0 ? "bg-[#111]" : "bg-[#151515]"
                            }
                          >
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2">
                                <HoverTip text={s.id}>
                                  <span className="font-mono text-xs whitespace-nowrap">
                                    {truncId(s.id)}
                                  </span>
                                </HoverTip>

                                {s.id && (() => {
                                  const key = `session-${s.id}`;
                                  const copied = copiedKey === key;

                                  return (
                                    <HoverTip text={copied ? "Copied!" : "Copy"}>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          copyToClipboard(s.id, key);
                                        }}
                                        aria-label="Copy session id"
                                        className={
                                          "h-6 w-6 shrink-0 inline-flex items-center justify-center rounded border transition " +
                                          (copied
                                            ? "border-green-500 text-green-300 bg-green-500/10"
                                            : "border-[#444] text-[#ddd] hover:border-[#38bdf8] hover:text-[#38bdf8]")
                                        }
                                      >
                                        <Copy className="w-3 h-3" />
                                      </button>
                                    </HoverTip>
                                  );
                                })()}
                              </div>
                            </td>


                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2">
                                <HoverTip text={s.build_id}>
                                  <span className="font-mono text-xs whitespace-nowrap">
                                    {truncId(s.build_id)}
                                  </span>
                                </HoverTip>

                                {s.build_id && (() => {
                                  const key = `build-${s.build_id}`;
                                  const copied = copiedKey === key;

                                  return (
                                    <HoverTip text={copied ? "Copied!" : "Copy"}>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          copyToClipboard(s.build_id, key);
                                        }}
                                        aria-label="Copy build id"
                                        className={
                                          "h-6 w-6 shrink-0 inline-flex items-center justify-center rounded border transition " +
                                          (copied
                                            ? "border-green-500 text-green-300 bg-green-500/10"
                                            : "border-[#444] text-[#ddd] hover:border-[#38bdf8] hover:text-[#38bdf8]")
                                        }
                                      >
                                        <Copy className="w-3 h-3" />
                                      </button>
                                    </HoverTip>
                                  );
                                })()}
                              </div>
                            </td>


                            <td className="px-3 py-2">
                              {s.tester_email || "Unknown"}
                            </td>
                            <td className="px-3 py-2">
                              {new Date(s.started_at).toLocaleString()}
                            </td>
                            <td className="px-3 py-2 text-[#aaa]">
                              {durationLabel}
                            </td>
                            <td className="px-3 py-2">
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${isActive
                                  ? "bg-green-900/40 text-green-300"
                                  : "bg-red-900/40 text-red-300"
                                  }`}
                              >
                                {isActive ? "Active" : "Ended"}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              <button
                                className="text-xs px-2 py-1 rounded border border-[#444] text-[#ddd] hover:border-[#38bdf8] hover:text-[#38bdf8]"
                                onClick={() =>
                                  navigate(`/dashboard/sessions/${s.id}`)
                                }
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                      {sessions.length === 0 && (
                        <tr>
                          <td
                            colSpan={7}
                            className="px-3 py-6 text-center text-[#777] text-sm"
                          >
                            No sessions yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 flex items-center justify-center">
                  {hasMoreSessions ? (
                    <button
                      onClick={loadMoreSessions}
                      className="text-sm px-3 py-1.5 rounded-lg border border-[#444] text-[#ddd] hover:border-[#38bdf8] hover:text-[#38bdf8]"
                    >
                      Load older sessions
                    </button>
                  ) : (
                    <div className="text-xs text-[#777]">No more sessions</div>
                  )}
                </div>
              </section>
            </div>
          )}
        </>
      )}

      {/* Edit game modal */}
      {showEditGame && game && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60">
          <div className="bg-[#151515] border border-[#333] rounded-xl p-5 w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-3">Edit game</h3>
            {editGameError && (
              <div className="mb-3 text-xs text-red-400 bg-red-900/30 border border-red-700 rounded px-3 py-2">
                {editGameError}
              </div>
            )}
            <form onSubmit={saveGameDetails} className="space-y-3">
              <div>
                <label className="block font-semibold text-base text-[#aaa] mb-1">
                  Game name
                </label>
                <input
                  type="text"
                  value={editGameName}
                  onChange={(e) => setEditGameName(e.target.value)}
                  className="w-full rounded bg-[#222] border border-[#333] px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#38bdf8]"
                  placeholder="My game title"
                />
              </div>
              <div className="pb-2">
                <label className="block font-semibold text-base text-[#aaa] mb-1">
                  Developer name{" "}
                  <span className="opacity-60">(optional)</span>
                </label>
                <input
                  type="text"
                  value={editDeveloperName}
                  onChange={(e) => setEditDeveloperName(e.target.value)}
                  className="w-full rounded bg-[#222] border border-[#333] px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#38bdf8]"
                  placeholder="Your name or studio"
                />
              </div>

              {/* Ready-signal checkbox + snippets */}
              <div className="">
                <label className="flex items-start gap-2 text-base text-[#aaa]">
                  <input
                    type="checkbox"
                    checked={editRequiresReadySignal}
                    onChange={(e) =>
                      setEditRequiresReadySignal(e.target.checked)
                    }
                    className="mt-0.5 h-4 w-4 rounded border-[#444] bg-[#222] accent-[#8338ec]"
                  />
                  <span>
                    <div className="font-semibold text-sm text-[#1f1f1f] bg-yellow-400 px-1 rounded w-fit mb-2">
                      Recommended!
                    </div>
                    <div className="font-semibold mb-1">
                      Only start session & video recordings after my game signals it&apos;s
                      ready.
                    </div>
                    <span className="text-[#888] text-sm">
                      When enabled, Playset waits for your build to call{" "}
                      <code className="px-1 py-0.5 rounded bg-black/40 border border-[#333] text-[10px]">
                        window.playsetGameReady()
                      </code>{" "}
                      before starting recordings. Otherwise, recordings
                      may include your loading screen and download time, and you will consume this as part of your monthly playtest session time allowance.
                    </span>
                  </span>
                </label>

                {editRequiresReadySignal && (
                  <>
                    {/* Helper snippets */}
                    < div className="mt-3 space-y-2">
                      <div className="text-sm text-[#aaa]">
                        Add one of these snippets to your first playable screen:
                      </div>
                      <div className="flex flex-row gap-2">
                        {/* Godot */}
                        <div className="border border-[#333] rounded-lg p-2 bg-black/30 flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[11px] font-semibold text-[#eee]">
                              Godot (Web export)
                            </span>
                            <button
                              type="button"
                              onClick={() => handleCopySnippet("godot")}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-[#444] text-[10px] text-[#ddd] hover:border-[#38bdf8] hover:text-[#38bdf8]"
                            >
                              <Copy className="w-3 h-3" />
                              {snippetCopied === "godot" ? "Copied!" : "Copy"}
                            </button>
                          </div>
                          <img
                            src="/godot_logo.svg"
                            alt="Godot logo"
                            className="h-12 object-contain mx-auto mt-2"
                          />
                          {/* <pre className="text-[10px] whitespace-pre-wrap bg-[#050505] border border-[#222] rounded px-2 py-1 overflow-x-auto">
                        {PLAYSET_READY_SNIPPETS.godot}
                      </pre> */}
                        </div>

                        {/* Unity */}
                        <div className="border border-[#333] rounded-lg p-2 bg-black/30 flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[11px] font-semibold text-[#eee]">
                              Unity (WebGL)
                            </span>
                            <button
                              type="button"
                              onClick={() => handleCopySnippet("unity")}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-[#444] text-[10px] text-[#ddd] hover:border-[#38bdf8] hover:text-[#38bdf8]"
                            >
                              <Copy className="w-3 h-3" />
                              {snippetCopied === "unity" ? "Copied!" : "Copy"}
                            </button>
                          </div>
                          <img
                            src="/unity_logo.svg"
                            alt="Godot logo"
                            className="h-12 object-contain invert mx-auto mt-2"
                          />
                          {/* <pre className="text-[10px] whitespace-pre-wrap bg-[#050505] border border-[#222] rounded px-2 py-1 overflow-x-auto">
                        {PLAYSET_READY_SNIPPETS.unity}
                      </pre> */}
                        </div>

                        {/* Plain JS */}
                        <div className="border border-[#333] rounded-lg p-2 bg-black/30 flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[11px] font-semibold text-[#eee]">
                              Plain JavaScript / HTML5
                            </span>
                            <button
                              type="button"
                              onClick={() => handleCopySnippet("js")}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-[#444] text-[10px] text-[#ddd] hover:border-[#38bdf8] hover:text-[#38bdf8]"
                            >
                              <Copy className="w-3 h-3" />
                              {snippetCopied === "js" ? "Copied!" : "Copy"}
                            </button>
                          </div>
                          <img
                            src="/js_logo.png"
                            alt="Godot logo"
                            className="h-12 object-contain rounded-lg mx-auto mt-2"
                          />
                          {/* <pre className="text-[10px] whitespace-pre-wrap bg-[#050505] border border-[#222] rounded px-2 py-1 overflow-x-auto">
                        {PLAYSET_READY_SNIPPETS.js}
                      </pre> */}
                        </div>
                      </div>
                    </div>
                  </>
                )}

              </div>

              <div className="mt-4 flex justify-between items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={savingGame || deletingGame}
                  className="text-xs px-3 py-1.5 rounded-lg border border-red-600 bg-red-600/20 text-red-400 hover:bg-red-600 hover:text-white disabled:opacity-60"
                >
                  Delete game
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={closeEditGame}
                    disabled={savingGame || deletingGame}
                    className="text-xs px-3 py-1.5 rounded-lg border border-[#444] text-[#ddd] hover:border-[#777] disabled:opacity-60"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingGame || deletingGame}
                    className="text-xs px-3 py-1.5 rounded-lg border border-[#38bdf8] bg-[#38bdf8]/20 text-[#e0f2fe] hover:bg-[#38bdf8] hover:text-black disabled:opacity-60"
                  >
                    {savingGame ? "Saving…" : "Save changes"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div >
      )
      }

      {/* Delete confirmation modal */}
      {showDeleteConfirm && game && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-[#151515] border border-red-600/50 rounded-xl p-5 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-red-400 mb-3">Delete game permanently?</h3>
            {deleteGameError && (
              <div className="mb-3 text-xs text-red-400 bg-red-900/30 border border-red-700 rounded px-3 py-2">
                {deleteGameError}
              </div>
            )}
            <p className="text-sm text-[#aaa] mb-4">
              This will permanently delete <span className="font-semibold">{game.name}</span> and all associated:
            </p>
            <ul className="text-xs text-[#bbb] mb-5 space-y-1 ml-4">
              <li>• All builds</li>
              <li>• All playtest sessions and recordings</li>
              <li>• All testers and invites</li>
              <li>• All session feedback</li>
            </ul>
            <p className="text-xs text-red-300 mb-4">
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deletingGame}
                className="text-xs px-3 py-1.5 rounded-lg border border-[#444] text-[#ddd] hover:border-[#777] disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={deleteGame}
                disabled={deletingGame}
                className="text-xs px-3 py-1.5 rounded-lg border border-red-600 bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
              >
                {deletingGame ? "Deleting…" : "Delete permanently"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription expired modal */}
      {subscriptionAccess && !subscriptionAccess.can_access && subscriptionAccess.plan_code && subscriptionAccess.plan_code !== 'free' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-[#151515] border border-yellow-600/50 rounded-xl p-6 w-full max-w-md shadow-xl space-y-4">
            <h3 className="text-lg font-semibold text-yellow-400">Subscription Expired</h3>
            <p className="text-sm text-[#aaa]">
              Your {subscriptionAccess.plan_code === 'starter' ? 'Starter' : subscriptionAccess.plan_code === 'studio' ? 'Studio' : 'paid'} subscription ended on{' '}
              <span className="font-semibold">
                {new Date(subscriptionAccess.expired_at).toLocaleDateString()}
              </span>.
            </p>
            <p className="text-sm text-[#aaa]">
              To access your games, sessions, and recordings, please renew your subscription.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => navigate('/purchase')}
                className="flex-1 text-sm px-4 py-2 rounded-lg bg-yellow-500 text-black font-semibold hover:bg-yellow-600"
              >
                Renew subscription
              </button>
              <button
                onClick={() => navigate('/')}
                className="flex-1 text-sm px-4 py-2 rounded-lg border border-[#444] text-[#ddd] hover:border-[#777]"
              >
                Go to home
              </button>
            </div>
          </div>
        </div>
      )}
    </div >
  );
}

